<template>
  <div class="acoustic-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F_rad = -∇U_acoustic$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>声源频率</label>
            <span class="val">40,000 <small>Hz</small></span>
          </div>
          <div class="hud-item orange">
            <label>悬浮稳定性</label>
            <span class="val" :style="{color: stabilityColor}">{{ stabilityText }}</span>
          </div>
        </div>

        <svg width="100%" height="280" viewBox="0 0 400 280" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-wave"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <radialGradient id="particle-grad"><stop offset="0%" stop-color="#fff" /><stop offset="100%" stop-color="#94a3b8" /></radialGradient>
          </defs>

          <!-- 发射头 & 反射板 -->
          <rect x="150" y="20" width="100" height="15" fill="#1e293b" rx="2" stroke="#334155" />
          <rect x="150" y="245" width="100" height="15" fill="#1e293b" rx="2" stroke="#334155" />
          <text x="200" y="32" text-anchor="middle" fill="#64748b" font-size="8" font-weight="900">TRANSDUCER</text>

          <!-- 驻波场视觉化 (Nodes and Antinodes) -->
          <g v-for="i in 5" :key="'w'+i" :transform="`translate(200, ${40 + i*40})`">
             <!-- 波腹 (高压区) -->
             <ellipse :rx="50" :ry="15" fill="none" stroke="#38bdf8" 
                      :stroke-width="intensity/15" :opacity="0.1 + (intensity/100)*0.3" 
                      :style="{ transform: `scale(${1 + Math.sin(t*10)*0.02})` }" />
             <!-- 波节 (稳定点标识) -->
             <line x1="-20" y1="0" x2="20" y2="0" stroke="#10b981" stroke-width="1" stroke-dasharray="2,2" opacity="0.3" />
          </g>

          <!-- 悬浮粒子群 -->
          <g v-for="(p, idx) in particles" :key="idx">
             <circle :cx="200 + p.offsetX + Math.sin(t*20 + idx)*1" 
                     :cy="p.y + Math.cos(t*15 + idx)*0.5" 
                     r="5" fill="url(#particle-grad)" filter="url(#glow-wave)" />
             <circle :cx="200 + p.offsetX" :cy="p.y" r="8" fill="rgba(255,255,255,0.05)" />
          </g>
        </svg>

        <div class="physics-caption">
           节点 (Node): 声压最低处，粒子受力平衡于此
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">超声波强度 (P_source)</label>
                 <el-slider v-model="intensity" :min="0" :max="100" size="small"></el-slider>
              </div>
              <el-button type="info" size="mini" plain @click="resetParticles">重置样本</el-button>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-help"></i>
           <p><strong>物理规律：</strong>声悬浮利用声波产生的<strong>辐射压</strong>。当超声波在封闭空间内反射形成驻波时，波节处的声压变化最小。微小物体由于受到来自上下波腹的压力挤压，会被锁定在由于声压梯度形成的“空气夹缝”中。<strong>观察：</strong>若强度低于 30%，重力将击败声压，悬浮将失效。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'AcousticLevitationViz',
  data() {
    return {
      intensity: 85,
      t: 0,
      particles: [
         { targetY: 60, y: 60, offsetX: -5 },
         { targetY: 100, y: 100, offsetX: 8 },
         { targetY: 140, y: 140, offsetX: -2 },
         { targetY: 180, y: 180, offsetX: 12 },
         { targetY: 220, y: 220, offsetX: -4 }
      ]
    }
  },
  computed: {
    stabilityText() {
       if (this.intensity > 70) return "固锁状态 (Locked)";
       if (this.intensity > 35) return "亚稳态 (Unstable)";
       return "悬浮失效 (Dropped)";
    },
    stabilityColor() {
       if (this.intensity > 70) return "#10b981";
       if (this.intensity > 35) return "#fbbf24";
       return "#f43f5e";
    }
  },
  methods: {
    resetParticles() {
       this.particles.forEach(p => { p.y = p.targetY; });
    },
    animate() {
       this.t += 0.05;
       
       // Physics simulation: Gravity vs Intensity
       const gravity = 1.2;
       const acousticLift = (this.intensity / 100) * 1.5;
       
       this.particles.forEach(p => {
          if (this.intensity < 30) {
             // Drop to bottom
             p.y = Math.min(240, p.y + gravity);
          } else {
             // Attempt to reach target node with slight drift if intensity is low
             const dist = p.targetY - p.y;
             p.y += dist * 0.1 + (gravity - acousticLift);
             // Bound check
             p.y = Math.max(40, Math.min(240, p.y));
          }
       });

       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); }
}
</script>

<style scoped lang="scss">
.acoustic-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 280px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f97316; } label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } } }
.physics-caption { position: absolute; bottom: 12px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #0c4a6e; } } }
</style>
